import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import AddingLightLoader from "../Loaders/AddingLightLoader";
import { fetchCustomerInvoice } from "../../store/Slices/CustomerInvoiceSlice";
import {
  GetCustomerInvoiceSingleApi,
  GetCustomerReturnInvoiceByIdApi,
} from "../../ApiRequests";
import { useReactToPrint } from "react-to-print";
import ThermalCustomerInvoice from "./ThermalCustomerInvoice";
import ThermalPrintHost from "../Thermal/ThermalPrintHost";
import { mapInvoiceToThermalReceipt } from "../../utils/thermalReceipt";
import { THERMAL_WIDTH_MM } from "../Thermal/thermalConfig";

const NewBillInvoice = () => {
  const [CurrentInvoiceData, setCurrentInvoiceData] = useState(null);
  const [thermalReceiptData, setThermalReceiptData] = useState(null);
  const { id: CurrentBillNo, cid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isReturnPrint = Boolean(location.state?.isReturn);
  const dispatch = useDispatch();
  const CustomerInvoices = useSelector((state) => state.CustomerInvoices);

  const thermalPrintRef = useRef(null);
  const printPendingRef = useRef(false);

  const handlePrint = useReactToPrint({
    content: () => thermalPrintRef.current,
    documentTitle: `Invoice-${thermalReceiptData?.invoice_no || CurrentBillNo || "receipt"}`,
    removeAfterPrint: true,
    onAfterPrint: () => {
      printPendingRef.current = false;
    },
  });

  const [loadError, setLoadError] = useState(false);
  const isGuestPrint = cid === "guest";

  useEffect(() => {
    setLoadError(false);
    if (isGuestPrint || isReturnPrint) {
      const load = isReturnPrint
        ? GetCustomerReturnInvoiceByIdApi(CurrentBillNo)
        : GetCustomerInvoiceSingleApi(CurrentBillNo);
      load
        .then((response) => {
          const invoice = response?.data?.data?.payload;
          if (invoice) setCurrentInvoiceData(invoice);
          else setLoadError(true);
        })
        .catch(() => setLoadError(true));
      return;
    }
    dispatch(fetchCustomerInvoice(cid));
  }, [cid, CurrentBillNo, dispatch, isGuestPrint, isReturnPrint]);

  useEffect(() => {
    if (isGuestPrint || isReturnPrint || !CustomerInvoices.data) return;
    const find = CustomerInvoices.data.find((dt) => dt._id === CurrentBillNo);
    setCurrentInvoiceData(find || null);
    if (CustomerInvoices.data.length && !find) setLoadError(true);
  }, [CustomerInvoices.data, CurrentBillNo, isGuestPrint, isReturnPrint]);

  useEffect(() => {
    if (!CurrentInvoiceData) return;
    setThermalReceiptData(
      mapInvoiceToThermalReceipt(CurrentInvoiceData, {
        isReturn: isReturnPrint || CurrentInvoiceData?.saleType === "RETURN",
      })
    );
  }, [CurrentInvoiceData, isReturnPrint]);

  useEffect(() => {
    if (!printPendingRef.current || !thermalReceiptData) return;
    printPendingRef.current = false;
    const timer = setTimeout(() => handlePrint(), 350);
    return () => clearTimeout(timer);
  }, [thermalReceiptData, handlePrint]);

  const queuePrint = () => {
    if (!CurrentInvoiceData) return;
    printPendingRef.current = true;
    setThermalReceiptData(
      mapInvoiceToThermalReceipt(CurrentInvoiceData, {
        isReturn: isReturnPrint || CurrentInvoiceData?.saleType === "RETURN",
      })
    );
  };

  if (loadError) {
    return (
      <div className="flex h-screen w-screen justify-center items-center text-inkSecondary">
        Invoice not found
      </div>
    );
  }

  if (!CurrentInvoiceData || !thermalReceiptData) {
    return (
      <div className="flex h-screen w-screen justify-center items-center">
        <AddingLightLoader />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-8 px-4 bg-page">
      <div className="flex items-center justify-center gap-3 relative w-full max-w-lg mb-6">
        <button
          type="button"
          className="border-2 border-ink px-2 py-2 rounded-full hover:text-white hover:bg-ink transition-colors ease-in-out duration-200 cursor-pointer absolute left-0"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft />
        </button>
        <button
          type="button"
          className="px-5 py-2.5 border-2 border-main bg-main text-white rounded-lg font-bold hover:bg-mainHover transition-colors active:scale-[.98]"
          onClick={queuePrint}
        >
          Print {THERMAL_WIDTH_MM}mm Thermal
        </button>
      </div>

      <div
        className="bg-card shadow-lifted ring-1 ring-black/10"
        style={{ width: `${THERMAL_WIDTH_MM}mm` }}
      >
        <ThermalCustomerInvoice data={thermalReceiptData} />
      </div>

      <ThermalPrintHost
        data={thermalReceiptData}
        printRef={thermalPrintRef}
        component={ThermalCustomerInvoice}
      />
    </div>
  );
};

export default NewBillInvoice;
